import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Package,
  Search,
  MapPin,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Truck,
  FileText,
  Phone,
} from 'lucide-react';
import { ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Badge } from '../components/ui/Badge';
import { useTranslation, useLanguage } from '../hooks/useTranslation';
import { StatutColis, EtapeDouane, HistoriqueStatut } from '../types';
import { colisService } from '../services/colisService';
import { isEcommerceType } from '../constants/ecommerce';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { api, API_BASE_URL } from '../config/api';

interface TrackingResult {
  id: string;
  tracking_number: string;
  destinataire_nom: string;
  destinataire_ville: string;
  statut_actuel: StatutColis;
  etape_douane?: EtapeDouane | null;
  localisation_actuelle?: string;
  date_creation: string;
  date_livraison?: string;
  tenant: {
    nom: string;
    logo_url?: string;
    type?: string;
  };
  // Coordonnées de l'agent de réception — spéc. MAJ 13/09 Cargo International §5
  agent_contact?: { nom: string; telephone: string } | null;
  historique: HistoriqueStatut[];
  // Créneau de livraison souhaité — self-service destinataire (spéc. 25/09/26)
  livraison_date_souhaitee?: string | null;
  livraison_creneau_souhaite?: 'matin' | 'apres_midi' | 'soir' | null;
  livraison_note_destinataire?: string | null;
}

const CRENEAU_FINAL_STATUTS: StatutColis[] = [StatutColis.LIVRE, StatutColis.ANNULE, StatutColis.RETOURNE];

export const PublicTracking: React.FC = () => {
  const [searchParams] = useSearchParams();
  const deepLinkTn = searchParams.get('tn')?.trim() || '';
  const [trackingNumber, setTrackingNumber] = useState(deepLinkTn);
  const [searchTerm, setSearchTerm] = useState('');
  const [showOtpEntry, setShowOtpEntry] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [showPrefForm, setShowPrefForm] = useState(false);
  const [prefDate, setPrefDate] = useState('');
  const [prefCreneau, setPrefCreneau] = useState<'' | 'matin' | 'apres_midi' | 'soir'>('');
  const [prefNote, setPrefNote] = useState('');
  const [prefSubmitting, setPrefSubmitting] = useState(false);
  const t = useTranslation();
  const { language } = useLanguage();
  const isEn = language !== 'fr';

  const CRENEAU_OPTIONS: { value: 'matin' | 'apres_midi' | 'soir'; label: string }[] = [
    { value: 'matin', label: isEn ? 'Morning (8am-12pm)' : 'Matin (8h-12h)' },
    { value: 'apres_midi', label: isEn ? 'Afternoon (12pm-4pm)' : 'Après-midi (12h-16h)' },
    { value: 'soir', label: isEn ? 'Evening (4pm-7pm)' : 'Soir (16h-19h)' },
  ];
  const creneauLabel = (v?: string | null) => CRENEAU_OPTIONS.find((c) => c.value === v)?.label || v;

  const { data: result, isLoading, error, refetch } = useQuery<TrackingResult>({
    queryKey: ['public-tracking', searchTerm],
    queryFn: async () => {
      const response = await api.get(`/colis/public/track/${searchTerm}`);
      return response.data;
    },
    enabled: false,
  });

  // Deep-link support (ex. lien de suivi dans un e-mail automatique) : /track?tn=CE-...
  useEffect(() => {
    if (deepLinkTn) {
      setSearchTerm(deepLinkTn);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTn]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      setSearchTerm(trackingNumber.trim());
      refetch();
    }
  };

  // Recours destinataire (v1.1 §3.II) : si le livreur refuse de saisir l'OTP,
  // le destinataire peut clôturer lui-même la livraison depuis cette page.
  const handleOtpSubmit = async () => {
    if (!result || otpInput.length < 4) return;
    setOtpSubmitting(true);
    try {
      await colisService.validateOtpPublic(result.tracking_number, otpInput);
      toast.success(isEn ? 'Delivery confirmed — thank you!' : 'Livraison confirmée — merci !');
      setShowOtpEntry(false);
      setOtpInput('');
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || (isEn ? 'Invalid code' : 'Code invalide'));
    } finally {
      setOtpSubmitting(false);
    }
  };

  // Créneau de livraison souhaité (self-service destinataire, spéc. 25/09/26) —
  // pré-remplit le formulaire d'édition avec la préférence déjà enregistrée.
  const openPrefForm = () => {
    if (result) {
      setPrefDate(result.livraison_date_souhaitee ? result.livraison_date_souhaitee.slice(0, 10) : '');
      setPrefCreneau(result.livraison_creneau_souhaite || '');
      setPrefNote(result.livraison_note_destinataire || '');
    }
    setShowPrefForm(true);
  };

  const handlePrefSubmit = async () => {
    if (!result) return;
    setPrefSubmitting(true);
    try {
      await colisService.setDeliveryPreferencePublic(result.tracking_number, {
        date_souhaitee: prefDate || undefined,
        creneau: prefCreneau || undefined,
        note: prefNote || undefined,
      });
      toast.success(t.tracking.deliveryPrefSaved);
      setShowPrefForm(false);
      refetch();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || (isEn ? 'Something went wrong' : 'Une erreur est survenue'));
    } finally {
      setPrefSubmitting(false);
    }
  };

  const getStatutIcon = (statut: string) => {
    switch (statut) {
      case StatutColis.LIVRE:
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case StatutColis.ANNULE:
        return <XCircle className="w-5 h-5 text-red-400" />;
      case StatutColis.EN_LIVRAISON:
      case StatutColis.LIVREUR_SUR_PLACE:
        return <Truck className="w-5 h-5 text-blue-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-orange-400" />;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold text-xl">CEDO Trace</span>
              </div>

              <LanguageSwitcher />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
              {t.tracking.title}
            </h1>
            <p className="text-xl text-white/80">
              {t.tracking.subtitle}
            </p>
          </div>

          {/* Search Form */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8 mb-8">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-white/60" />
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder={t.tracking.placeholder}
                  className="w-full pl-14 pr-4 py-4 bg-white/10 border border-white/30 rounded-xl text-white text-lg placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-white text-blue-600 font-bold text-lg rounded-xl hover:bg-white/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {isLoading ? t.tracking.searching : t.tracking.searchButton}
              </button>
            </form>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/20 backdrop-blur-xl rounded-2xl border border-red-500/30 p-6 mb-8">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-300" />
                <div>
                  <h3 className="text-white font-semibold">
                    {t.tracking.notFound}
                  </h3>
                  <p className="text-white/80 text-sm">
                    {t.tracking.notFoundDesc}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-6">
              {/* Company Branding */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
                <div className="flex items-center gap-4">
                  {result.tenant.logo_url && (
                    <img
                      src={result.tenant.logo_url}
                      alt={result.tenant.nom}
                      className="w-16 h-16 rounded-xl object-cover" />
                  )}
                  <div>
                    <p className="text-white/70 text-sm">{t.tracking.managedBy}</p>
                    <h2 className="text-2xl font-bold text-white">
                      {result.tenant.nom}
                    </h2>
                  </div>
                </div>
              </div>

              {/* Tracking Info */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <p className="text-white/70 text-sm mb-1">
                      {t.tracking.trackingNumber}
                    </p>
                    <p className="text-2xl font-bold text-white font-mono">
                      {result.tracking_number}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge statut={result.statut_actuel} />
                    {result.etape_douane && (
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        {t.etapesDouane[result.etape_douane]}
                      </span>
                    )}
                    <a
                      href={`${API_BASE_URL}/colis/public/track/${encodeURIComponent(result.tracking_number)}/bordereau`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Bordereau
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-purple-400" />
                    <div>
                      <p className="text-white/60 text-xs">{t.tracking.recipient}</p>
                      <p className="text-white">{result.destinataire_nom}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-pink-400" />
                    <div>
                      <p className="text-white/60 text-xs">{t.tracking.destination}</p>
                      <p className="text-white">{result.destinataire_ville}</p>
                    </div>
                  </div>

                  {result.localisation_actuelle && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white/60 text-xs">
                          {t.tracking.currentLocation}
                        </p>
                        <p className="text-white">{result.localisation_actuelle}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-white/60 text-xs">{t.tracking.createdOn}</p>
                      <p className="text-white">
                        {format(new Date(result.date_creation), 'dd MMM yyyy', {
                          locale: language === 'fr' ? fr : enUS,
                        })}
                      </p>
                    </div>
                  </div>

                  {result.agent_contact && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-cyan-400" />
                      <div>
                        <p className="text-white/60 text-xs">
                          {isEn ? 'Your local agent' : 'Votre agent local'}
                        </p>
                        <p className="text-white">
                          {result.agent_contact.nom} — {result.agent_contact.telephone}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recours destinataire — validation OTP si le livreur refuse de le faire (v1.1 §3.II) */}
              {result.statut_actuel === StatutColis.EN_LIVRAISON && (
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
                  {!showOtpEntry ? (
                    <button
                      type="button"
                      onClick={() => setShowOtpEntry(true)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="flex items-center gap-2 text-white/80 text-sm">
                        <ShieldCheck className="w-4 h-4 text-green-400" />
                        {isEn
                          ? "Delivery agent didn't ask for your code? Confirm it yourself"
                          : 'Le livreur ne vous a pas demandé votre code ? Confirmez vous-même'}
                      </span>
                      <span className="text-purple-300 text-sm">→</span>
                    </button>
                  ) : (
                    <div>
                      <p className="text-white/70 text-sm mb-3">
                        {isEn
                          ? 'Enter the 4-digit code sent by SMS to confirm you received your parcel.'
                          : 'Entrez le code à 4 chiffres reçu par SMS pour confirmer la réception de votre colis.'}
                      </p>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={otpInput}
                          onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ''))}
                          placeholder="0000"
                          className="flex-1 px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white text-center text-xl font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <button
                          type="button"
                          onClick={handleOtpSubmit}
                          disabled={otpInput.length < 4 || otpSubmitting}
                          className="px-5 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors"
                        >
                          {otpSubmitting ? '...' : (isEn ? 'Confirm' : 'Confirmer')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Créneau de livraison souhaité — self-service destinataire (E-Commerce, spéc. 25/09/26) */}
              {isEcommerceType(result.tenant.type) && !CRENEAU_FINAL_STATUTS.includes(result.statut_actuel) && (
                <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5 text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{t.tracking.deliveryPrefTitle}</h3>
                  </div>

                  {!showPrefForm ? (
                    <div>
                      {(result.livraison_date_souhaitee || result.livraison_creneau_souhaite) ? (
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-white/60 text-xs">{t.tracking.deliveryPrefCurrent}</p>
                            <p className="text-white">
                              {result.livraison_date_souhaitee &&
                                format(new Date(result.livraison_date_souhaitee), 'dd MMM yyyy', {
                                  locale: language === 'fr' ? fr : enUS,
                                })}
                              {result.livraison_date_souhaitee && result.livraison_creneau_souhaite ? ' — ' : ''}
                              {creneauLabel(result.livraison_creneau_souhaite)}
                            </p>
                            {result.livraison_note_destinataire && (
                              <p className="text-white/60 text-sm italic mt-1">"{result.livraison_note_destinataire}"</p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={openPrefForm}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap"
                          >
                            {t.tracking.deliveryPrefEdit}
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-white/70 text-sm mb-3">{t.tracking.deliveryPrefDesc}</p>
                          <button
                            type="button"
                            onClick={openPrefForm}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl transition-colors"
                          >
                            {t.tracking.deliveryPrefSave}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-white/70 text-sm">{t.tracking.deliveryPrefDesc}</p>
                      <div>
                        <label className="block text-white/60 text-xs mb-1">{t.tracking.deliveryPrefDateLabel}</label>
                        <input
                          type="date"
                          value={prefDate}
                          min={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setPrefDate(e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                        />
                      </div>
                      <div>
                        <label className="block text-white/60 text-xs mb-1">{t.tracking.deliveryPrefSlotLabel}</label>
                        <select
                          value={prefCreneau}
                          onChange={(e) => setPrefCreneau(e.target.value as any)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                        >
                          <option className="bg-slate-800" value="">{t.tracking.deliveryPrefSlotAny}</option>
                          {CRENEAU_OPTIONS.map((c) => (
                            <option className="bg-slate-800" key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-white/60 text-xs mb-1">{t.tracking.deliveryPrefNoteLabel}</label>
                        <textarea
                          value={prefNote}
                          onChange={(e) => setPrefNote(e.target.value.slice(0, 300))}
                          placeholder={t.tracking.deliveryPrefNotePlaceholder}
                          rows={2}
                          className="w-full px-3 py-2 bg-white/10 border border-white/30 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handlePrefSubmit}
                          disabled={prefSubmitting}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
                        >
                          {prefSubmitting ? '...' : t.tracking.deliveryPrefSave}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPrefForm(false)}
                          className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white/80 text-sm rounded-xl transition-colors"
                        >
                          {isEn ? 'Cancel' : 'Annuler'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6">
                <h3 className="text-xl font-semibold text-white mb-6">
                  {t.tracking.timeline}
                </h3>

                {result.historique && result.historique.length > 0 ? (
                  <div className="space-y-4">
                    {result.historique.map((h: HistoriqueStatut, index: number) => (
                      <div key={h.id} className="relative">
                        {index < result.historique.length - 1 && (
                          <div className="absolute left-[11px] top-8 w-0.5 h-full bg-white/20" />
                        )}

                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 border-2 border-purple-400 flex items-center justify-center relative z-10">
                            {getStatutIcon(h.statut)}
                          </div>

                          <div className="flex-1 pb-6">
                            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Badge statut={h.statut as StatutColis} />
                                  {h.etape_douane && (
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                      {t.etapesDouane[h.etape_douane as EtapeDouane]}
                                    </span>
                                  )}
                                </div>
                                <span className="text-white/50 text-xs">
                                  {format(new Date(h.timestamp), 'dd/MM HH:mm')}
                                </span>
                              </div>

                              {h.localisation && (
                                <p className="text-white/70 text-sm mb-1 flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  {h.localisation}
                                </p>
                              )}

                              {h.commentaire && (
                                <p className="text-white/60 text-sm italic">
                                  "{h.commentaire}"
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/50 text-center py-8">
                    {t.tracking.noHistory}
                  </p>
                )}
              </div>
            </div>
		)}

          {/* Info Cards */}
          {!result && !error && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Search className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">
                  {t.tracking.feature1Title}
                </h3>
                <p className="text-white/70 text-sm">
                  {t.tracking.feature1Desc}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">
                  {t.tracking.feature2Title}
                </h3>
                <p className="text-white/70 text-sm">
                  {t.tracking.feature2Desc}
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-6 text-center">
                <div className="w-12 h-12 bg-white/20 rounded-xl mx-auto mb-4 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">
                  {t.tracking.feature3Title}
                </h3>
                <p className="text-white/70 text-sm">
                  {t.tracking.feature3Desc}
                </p>
              </div>
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center text-white/60 text-sm">
          <p>© 2026 CEDO Trace - {t.tracking.footer}</p>
        </footer>
      </div>
    </div>
  );
};
